import { c as _c } from "react/compiler-runtime";
import { useTimezone } from "@/hooks/use-timezone";
import { segmentHeaderClassName, useCalendarTextSize } from "@/lib/calendar-text-size";
import { formatTime } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { EventCleanupSegmentTiny } from "@/types/event";
import { SEGMENT_TITLE_STYLE } from "./calendar-event/appointment-service-segment-card";

/** Default color for cleanup time segments when no color is specified */
export const CLEANUP_TIME_DEFAULT_COLOR = "hsl(var(--foreground-tertiary))";
interface CleanupTimeSliceProps {
  cleanupTime: EventCleanupSegmentTiny;
  /** Start percentage (0-100) from top of container */
  topPct: number;
  /** End percentage (0-100) from bottom of container */
  bottomPct: number;
  /** Additional styles to apply */
  style?: React.CSSProperties;
  /** Optional class name override */
  className?: string;
  /** Patient name to display after cleanup label */
  patientName?: string;
}
export function CleanupTimeSlice(t0) {
  const $ = _c(68);
  const {
    cleanupTime,
    topPct,
    bottomPct,
    style,
    className,
    patientName
  } = t0;
  const timeZone = useTimezone();
  const textSize = useCalendarTextSize();
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== bottomPct || $[1] !== className || $[2] !== cleanupTime.color || $[3] !== cleanupTime.end || $[4] !== cleanupTime.start || $[5] !== cleanupTime.title || $[6] !== patientName || $[7] !== style || $[8] !== textSize || $[9] !== timeZone || $[10] !== topPct) {
    const cleanupStart = new Date(cleanupTime.start);
    const cleanupEnd = new Date(cleanupTime.end);
    const customColor = cleanupTime.color;
    const cleanupDurationMinutes = (cleanupEnd.getTime() - cleanupStart.getTime()) / 60000;
    const t13 = cleanupDurationMinutes <= 15 ? "overflow-hidden whitespace-nowrap" : cleanupDurationMinutes <= 20 ? "line-clamp-2 break-words" : "break-words";
    const t14 = !customColor && "border-foreground-tertiary/25 bg-foreground-tertiary/20";
    if ($[23] !== className || $[24] !== t13 || $[25] !== t14) {
      t11 = cx("pointer-events-auto absolute left-0 right-0 overflow-hidden rounded-md [container-type:size] border", t13, t14, className);
      $[23] = className;
      $[24] = t13;
      $[25] = t14;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    const t15 = `${topPct}%`;
    const t16 = `${bottomPct}%`;
    let t17;
    if ($[27] !== customColor) {
      t17 = customColor && {
        backgroundColor: `${customColor}50`,
        borderColor: `${customColor}40`
      };
      $[27] = customColor;
      $[28] = t17;
    } else {
      t17 = $[28];
    }
    if ($[29] !== style || $[30] !== t15 || $[31] !== t16 || $[32] !== t17) {
      t12 = {
        top: t15,
        bottom: t16,
        ...t17,
        ...style
      };
      $[29] = style;
      $[30] = t15;
      $[31] = t16;
      $[32] = t17;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    const t18 = !customColor && "border-l-foreground-tertiary";
    if ($[34] !== t18) {
      t9 = cx("flex h-full rounded-l-md border-l-[3px]", t18);
      $[34] = t18;
      $[35] = t9;
    } else {
      t9 = $[35];
    }
    if ($[36] !== customColor) {
      t10 = customColor ? {
        borderLeftColor: customColor
      } : undefined;
      $[36] = customColor;
      $[37] = t10;
    } else {
      t10 = $[37];
    }
    t8 = "ml-1";
    if ($[38] !== textSize) {
      t6 = segmentHeaderClassName(textSize);
      $[38] = textSize;
      $[39] = t6;
    } else {
      t6 = $[39];
    }
    const t19 = cleanupTime.title ?? "Cleanup";
    let t20;
    if ($[40] !== t19) {
      t20 = <span>{t19}</span>;
      $[40] = t19;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    const t21 = patientName && ` - ${patientName}`;
    let t22;
    if ($[42] !== t21) {
      t22 = <span>{t21}</span>;
      $[42] = t21;
      $[43] = t22;
    } else {
      t22 = $[43];
    }
    if ($[44] !== t20 || $[45] !== t22) {
      t7 = <div className="overflow-hidden text-calendar-tiny font-medium leading-tight" style={SEGMENT_TITLE_STYLE}>{t20}{t22}</div>;
      $[44] = t20;
      $[45] = t22;
      $[46] = t7;
    } else {
      t7 = $[46];
    }
    t1 = "text-calendar-micro opacity-80";
    t2 = formatTime(cleanupStart, false, timeZone);
    t3 = " -";
    t4 = " ";
    t5 = formatTime(cleanupEnd, false, timeZone);
    $[0] = bottomPct;
    $[1] = className;
    $[2] = cleanupTime.color;
    $[3] = cleanupTime.end;
    $[4] = cleanupTime.start;
    $[5] = cleanupTime.title;
    $[6] = patientName;
    $[7] = style;
    $[8] = textSize;
    $[9] = timeZone;
    $[10] = topPct;
    $[11] = t1;
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t1 = $[11];
    t10 = $[12];
    t11 = $[13];
    t12 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  let t13;
  if ($[47] !== t1 || $[48] !== t2 || $[49] !== t3 || $[50] !== t4 || $[51] !== t5) {
    t13 = <div className={t1}>{t2}{t3}{t4}{t5}</div>;
    $[47] = t1;
    $[48] = t2;
    $[49] = t3;
    $[50] = t4;
    $[51] = t5;
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  let t14;
  if ($[53] !== t13 || $[54] !== t6 || $[55] !== t7) {
    t14 = <div className={t6}>{t7}{t13}</div>;
    $[53] = t13;
    $[54] = t6;
    $[55] = t7;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== t14 || $[58] !== t8) {
    t15 = <div className={t8}>{t14}</div>;
    $[57] = t14;
    $[58] = t8;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  let t16;
  if ($[60] !== t10 || $[61] !== t15 || $[62] !== t9) {
    t16 = <div className={t9} style={t10}>{t15}</div>;
    $[60] = t10;
    $[61] = t15;
    $[62] = t9;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  let t17;
  if ($[64] !== t11 || $[65] !== t12 || $[66] !== t16) {
    t17 = <div className={t11} style={t12}>{t16}</div>;
    $[64] = t11;
    $[65] = t12;
    $[66] = t16;
    $[67] = t17;
  } else {
    t17 = $[67];
  }
  return t17;
}